/** 구독 스트림 2개. 앱 전체에서 각 1개만 존재한다. */
export type StreamName = 'events' | 'safety'

export const STREAM_NAMES: readonly StreamName[] = ['events', 'safety']

export type StreamStatus = 'idle' | 'connecting' | 'open' | 'closed'

export interface StreamState {
  status: StreamStatus
  /** 마지막으로 서버 메시지를 받은 시각(epoch ms). 아직 없으면 null */
  lastMessageAt: number | null
  /**
   * 이 스트림이 주는 집합을 현재 값으로 믿을 수 없는 상태.
   * true 면 상태변경 버튼을 전부 비활성한다 (마지막 캐시로 조작 금지).
   *
   * 판정 입력은 둘이다.
   * 1. **연결 상태** — `open` 이 아니면 stale. 임계값이 필요 없다.
   * 2. **무소식 경과** — `realtimeConfig().staleThresholdMs` 가 **있을 때만** 본다.
   *    🔴 임계가 `undefined` 면 이 축을 **판정하지 않는다**(추측값 금지 · 계약 미확정).
   */
  stale: boolean
}

export interface RealtimeState {
  events: StreamState
  safety: StreamState
  /** 둘 중 하나라도 stale 이면 제어 불가. 화면은 이 값만 보면 된다. */
  controlDisabled: boolean
  /**
   * **끊겼다** — 한 번 붙었던 채널이 stale 이 됐다(닫힘 · 무소식). 「연결 끊김」 띠는 이것을 본다.
   * 🔴 `controlDisabled` 와 다르다: 새로고침 직후 아직 붙는 중(`connecting`)은 제어 불가이지만 «끊긴» 것은 아니다 —
   *    그때 띠를 띄우면 화면마다 열 때마다 붉은 줄이 번쩍인다(eric 2026-09-22 「관리 메뉴에서 리프레시하면 연결 끊김이
   *    떴다가 사라지는데 안 뜨게」). 한 번도 못 붙은 채널은 «아직»이지 «끊김»이 아니다.
   */
  outage: boolean
}

/** 연결 한 개의 수명주기. 구현은 앱 셸(RealtimeProvider)만 만든다. */
export interface StreamConnection {
  readonly name: StreamName
  /** 영구 종료. 재연결 타이머까지 끈다 */
  close(): void
}

export interface StreamConnectionOptions {
  name: StreamName
  url: string
  /** 연결 상태가 바뀔 때마다. 재연결 시도도 `connecting` 으로 올라온다 */
  onStatus: (status: StreamStatus) => void
  /**
   * 서버 메시지 1건. JSON 이면 파싱된 값, 아니면 원문 문자열이 온다.
   * 🔴 여기서 쿼리 캐시를 건드리지 않는다 — realtime 은 쿼리 키를 모른다(`@ctcells/api` 의 브리지 몫).
   */
  onMessage: (raw: unknown) => void
}

/**
 * 실제 연결을 만드는 자리. 표준 브라우저 WebSocket 으로 구현한다
 * (데스크톱 셸에서도 같은 코드를 쓴다 — 별도 WS 플러그인 쓰지 않음).
 *
 * 🔴 **이 자리가 `live` / `demo` 가 갈리는 유일한 지점이다.** 목은 연결 계층에서만 갈리고
 *    화면·게이트·상태 판정은 실물과 완전히 같은 코드를 탄다.
 */
export type StreamConnectionFactory = (options: StreamConnectionOptions) => StreamConnection

/** 구독 해제 함수 */
export type Unsubscribe = () => void

/** 원시 이벤트 구독. `@ctcells/api` 의 스트림 브리지가 이 위에 얹힌다 */
export type StreamSubscribe = (name: StreamName, listener: (raw: unknown) => void) => Unsubscribe

export interface RealtimeContextValue {
  state: RealtimeState
  subscribe: StreamSubscribe
}
